import { FormikProps } from 'formik';
import { CreateIdeaStopFormData } from '../../../../../../types/ideaStop.types';
import { shouldShowIdeaStopField, getIdeaStopFieldInputType } from '../../../../../../types/ideaStop.config';
import CountrySelect from '../../../../../../common/CountrySelect';
import AirportSelect from '../../../../../../common/AirportSelect';
import AirlineSelect from '../../../../../../common/AirlineSelect';
import GooglePlacesInput from '../../../../../../common/GooglePlacesInput';
import CustomInput from '../../../../../../common/CustomInput';

/**
 * Hook for rendering form fields for idea stops
 * @param formik - Formik props object
 * @param ideaStopType - Current idea stop type
 * @returns Object with field rendering utilities
 */
export const useFormFieldRenderer = (formik: FormikProps<CreateIdeaStopFormData>, ideaStopType: string) => {
  const { values, handleChange, errors, touched, setFieldValue, setFieldTouched } = formik;

  const isFieldVisible = (fieldName: string) => {
    return shouldShowIdeaStopField(fieldName, ideaStopType);
  };

  const getError = (fieldName: keyof CreateIdeaStopFormData) => {
    return touched[fieldName] && errors[fieldName] ? String(errors[fieldName]) : '';
  };

  const renderFieldInput = (fieldName: keyof CreateIdeaStopFormData, label: string, placeholder?: string) => {
    const inputType = getIdeaStopFieldInputType(fieldName as string);
    const fieldValue = values[fieldName] !== undefined && values[fieldName] !== null ? String(values[fieldName]) : '';
    const error = getError(fieldName);

    switch (inputType) {
      case 'country':
        return (
          <CountrySelect
            name={fieldName}
            inputLabel={label}
            value={fieldValue}
            onChange={handleChange}
            error={error}
          />
        );

      case 'airport':
        return (
          <AirportSelect
            name={fieldName}
            inputLabel={label}
            value={fieldValue}
            onChange={handleChange}
            onDetailsChange={details => {
              setFieldValue(`${fieldName}_details`, details);
              setFieldValue(fieldName, details?.iata_code ?? '');
            }}
            details={values[`${fieldName}_details` as keyof CreateIdeaStopFormData] as { name: string; iata_code: string } | undefined}
            error={error}
          />
        );

      case 'airline':
        return (
          <AirlineSelect
            name={fieldName}
            inputLabel={label}
            value={fieldValue}
            onChange={handleChange}
            onDetailsChange={details => {
              setFieldValue(`${fieldName}_details`, details);
              setFieldValue(fieldName, details?.iata_code ?? '');
            }}
            error={error}
          />
        );

      case 'address':
        return (
          <GooglePlacesInput
            name={fieldName}
            inputLabel={label}
            value={fieldValue}
            onChange={(address: string, placeDetails?: google.maps.places.PlaceResult) => {
              setFieldValue(fieldName, address);

              if (placeDetails) {
                setFieldValue(`${fieldName}_details`, placeDetails);
              } else {
                setFieldValue(`${fieldName}_details`, null);
              }
            }}
            onBlur={() => setFieldTouched(fieldName, true)}
            placeholder={placeholder || 'Enter address...'}
            error={!!error}
            helperText={error}
          />
        );

      default:
        return (
          <CustomInput
            inputName={fieldName}
            inputLabel={label}
            value={fieldValue}
            onChange={handleChange}
            error={error}
            placeholder={placeholder}
          />
        );
    }
  };

  return {
    isFieldVisible,
    getError,
    renderFieldInput,
  };
};
